import {
  PolicyList,
  PolicyPage,
  PolicySection,
  PolicySubsection,
} from '../../components/PolicyPage';

const EFFECTIVE_DATE = 'May 20, 2026';
const LAST_UPDATED = 'October 6, 2026';

export function Privacy() {
  return (
    <PolicyPage
      seoTitle="Privacy Policy — AuctiSync"
      seoDescription="How Welcome Home Apps LLC collects, uses, shares, and protects information at AuctiSync."
      canonicalPath="/privacy"
      title="Privacy Policy"
      effectiveDate={EFFECTIVE_DATE}
      lastUpdated={LAST_UPDATED}
    >
      <p>
        Welcome Home Apps LLC (&ldquo;Welcome Home Apps,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo;
        or &ldquo;our&rdquo;) operates the AuctiSync auction platform (the &ldquo;Services&rdquo;).
        This Privacy Policy describes what information we collect, how we use it, who we share it
        with, and the choices you have. It applies to our websites (<strong>auctisync.com</strong>,{' '}
        <strong>app.auctisync.com</strong> and <strong>help.auctisync.com</strong>), auction
        houses&apos; own domains connected to AuctiSync, and our iOS and Android applications.
      </p>

      <p>
        Please also review our <a href="/terms">Terms of Service</a>, which governs your use of the
        Services.
      </p>

      <PolicySection title="1. Who We Are">
        <p>
          Welcome Home Apps LLC is a Missouri limited liability company. For privacy questions,
          contact <a href="mailto:support@auctisync.com">support@auctisync.com</a>.
        </p>
      </PolicySection>

      <PolicySection title="2. Information We Collect">
        <PolicySubsection title="2.1 Information You Give Us">
          <PolicyList
            items={[
              <span>
                <strong>Account information</strong>: name, email address, phone number, password
                hash, and (for tenants) business name + billing address.
              </span>,
              <span>
                <strong>Identity verification</strong>: when required for tenant onboarding (Stripe
                Connect), we collect identity-verification information through our payments
                processor (Stripe). We do not store full Social Security Numbers, driver&apos;s
                license numbers, or scans of identity documents on our servers; those are submitted
                directly to Stripe.
              </span>,
              <span>
                <strong>Payment information</strong>: credit card and ACH details for bidders are
                tokenized and stored by Stripe. We retain only the last four digits, the card brand,
                and an opaque Stripe customer ID.
              </span>,
              <span>
                <strong>Auction content</strong>: photos, descriptions, titles, and other content
                tenants upload about lots being auctioned, plus questions and comments bidders post.
              </span>,
              <span>
                <strong>Communications</strong>: emails or messages you send to support, including
                attachments.
              </span>,
            ]}
          />
        </PolicySubsection>

        <PolicySubsection title="2.2 Information We Collect Automatically">
          <PolicyList
            items={[
              <span>
                <strong>Usage data</strong>: pages and screens viewed, lots viewed, bids placed,
                watchlist activity, search terms, and timestamps.
              </span>,
              <span>
                <strong>Device + technical data</strong>: IP address, browser type and version,
                operating system, mobile device model, app version, language, and approximate
                geographic location (city/region level, derived from IP).
              </span>,
              <span>
                <strong>Crash + diagnostic data</strong>: stack traces, performance metrics, and
                error context, collected via Sentry. We strip authorization headers, cookies, and
                request bodies before transmission.
              </span>,
              <span>
                <strong>Cataloging app diagnostics</strong>: the AuctiSync Cataloging app, used by
                auction-house staff, keeps a short log of how the app is being used &mdash; screens
                opened, taps and swipes and where they landed, photos taken (a count, not the
                photos), uploads, whether the device was on Wi-Fi or mobile data, and errors. If the
                app stops responding to touch, or you tap &ldquo;Report a problem,&rdquo; it sends
                that log to us with your account, device model, operating system, app version, the
                sale and lot you were on, and any note you add. It never includes lot titles,
                descriptions, prices, or other text you typed. We use it only to find and fix
                problems in the app.
              </span>,
              <span>
                <strong>Cookies + similar technologies</strong>: see Section 8.
              </span>,
            ]}
          />
        </PolicySubsection>

        <PolicySubsection title="2.3 Information From Third Parties">
          <p>
            Stripe tells us whether a Tenant&apos;s identity verification and payout setup are
            complete (see 2.1). We do not receive information about you from other third parties for
            advertising or profiling.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="3. How We Use Your Information">
        <PolicyList
          items={[
            <span>To provide, operate, and maintain the Services.</span>,
            <span>
              To let you use a single AuctiSync account across multiple auction houses &mdash;
              including viewing and switching between the auction houses your account is registered
              with.
            </span>,
            <span>
              To process bids, run auctions, send invoices, settle payments, and disburse funds to
              tenants.
            </span>,
            <span>
              To send transactional communications: account confirmations, outbid notices,
              auction-ending alerts, invoice notifications, password resets, and tenant broadcasts.
            </span>,
            <span>
              To send service-related operational communications (e.g., security advisories, changes
              to terms, downtime notices).
            </span>,
            <span>
              To detect, investigate, and prevent fraud, shill bidding, abuse, and policy
              violations.
            </span>,
            <span>
              To comply with legal obligations, including record-keeping and tax-reporting
              requirements.
            </span>,
            <span>
              To analyze aggregated, de-identified platform metrics to improve the Services.
            </span>,
            <span>
              To keep the Services safe, including reviewing uploaded images and content for
              prohibited material and reporting apparent child sexual abuse material to the National
              Center for Missing &amp; Exploited Children (NCMEC) as required by 18 U.S.C. § 2258A.
            </span>,
          ]}
        />
        <p>
          We do <strong>not</strong> use bidder personal data to train artificial intelligence or
          machine-learning models that are exposed across tenants, and we do not sell your personal
          information to third parties.
        </p>
      </PolicySection>

      <PolicySection title="4. How We Share Your Information">
        <PolicySubsection title="4.1 With Tenants and Bidders">
          <PolicyList
            items={[
              <span>
                Bidders&apos; usernames and bid amounts are visible to the tenant operating an
                auction (auction operator), and to other bidders to the extent shown in our auction
                interfaces (typically aggregate high-bid display, not a public bidder roster).
              </span>,
              <span>
                When a bidder wins a lot, the tenant receives the bidder&apos;s name, email,
                shipping or pickup address, and contact phone number for fulfillment.
              </span>,
              <span>
                Questions a bidder posts on a lot are visible to the tenant and may be displayed
                publicly along with the tenant&apos;s answer, if the tenant elects to publish.
              </span>,
            ]}
          />
        </PolicySubsection>

        <PolicySubsection title="4.2 Service Providers (Sub-Processors)">
          <p>
            We rely on the following third-party service providers, each contractually obligated to
            handle your data only as needed to provide their services:
          </p>
          <PolicyList
            items={[
              <span>
                <strong>Stripe, Inc.</strong> — payment processing, Stripe Connect tenant payouts,
                identity verification. See{' '}
                <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
                  Stripe&apos;s Privacy Policy
                </a>
                .
              </span>,
              <span>
                <strong>Supabase, Inc.</strong> — authentication, primary database, file storage.
                See{' '}
                <a href="https://supabase.com/privacy" target="_blank" rel="noreferrer">
                  Supabase&apos;s Privacy Policy
                </a>
                .
              </span>,
              <span>
                <strong>Cloudflare, Inc.</strong> — content delivery, edge security, Workers
                compute, R2 object storage. See{' '}
                <a
                  href="https://www.cloudflare.com/privacypolicy/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Cloudflare&apos;s Privacy Policy
                </a>
                .
              </span>,
              <span>
                <strong>Functional Software, Inc. (Sentry)</strong> — application error and
                performance monitoring.
              </span>,
              <span>
                <strong>Google LLC (Firebase Cloud Messaging)</strong> — push notification delivery
                to mobile devices.
              </span>,
              <span>
                <strong>Resend, Inc.</strong> — transactional email delivery.
              </span>,
              <span>
                <strong>Anthropic PBC</strong> — AI-assisted features: the Help Center assistant,
                the auction-house assistant, and AI drafting of lot descriptions. Tenant content
                used in AI prompts is not used to train Anthropic&apos;s models.
              </span>,
              <span>
                <strong>OpenAI, L.L.C.</strong> — converts lot titles and descriptions into search
                data (embeddings) for search and price comparisons. No personal information is sent.
              </span>,
              <span>
                <strong>Apple Inc.</strong> (Apple Push Notification service) — delivers push
                notifications and Live Activities to Apple devices.
              </span>,
            ]}
          />
          <p>
            We will publish material updates to this sub-processor list at this URL and notify
            tenants by email of additions.
          </p>
        </PolicySubsection>

        <PolicySubsection title="4.3 Legal and Safety Disclosures">
          <p>
            We may disclose information if required to do so by law, subpoena, or court order; to
            comply with legal obligations such as 18 U.S.C. § 2258A child-safety reporting; to
            enforce our <a href="/terms">Terms of Service</a>; to investigate potential fraud or
            policy violations; or to protect the rights, property, or safety of Welcome Home Apps,
            our users, or the public.
          </p>
        </PolicySubsection>

        <PolicySubsection title="4.4 Business Transfers">
          <p>
            If Welcome Home Apps is involved in a merger, acquisition, financing, reorganization,
            bankruptcy, or sale of all or a portion of its assets, your information may be
            transferred to the successor entity. We will notify affected users by email of any
            change in the entity controlling your data.
          </p>
        </PolicySubsection>

        <PolicySubsection title="4.5 What We Do Not Do">
          <PolicyList
            items={[
              <span>We do not sell your personal information.</span>,
              <span>We do not share your personal information with advertisers.</span>,
              <span>
                We do not use your personal information to train cross-tenant AI or machine-learning
                models.
              </span>,
              <span>
                We do not pool bidder personal data into analytical engines exposed to other
                tenants.
              </span>,
            ]}
          />
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="5. Data Retention">
        <PolicyList
          items={[
            <span>
              <strong>Account records</strong>: retained for the life of the account, plus seven (7)
              years after closure, to satisfy auction-industry recordkeeping requirements, tax
              reporting (IRS 1099-K), and dispute-resolution windows.
            </span>,
            <span>
              <strong>Auction records</strong> (bids, lot data, invoices): retained for seven (7)
              years after the auction closes.
            </span>,
            <span>
              <strong>Diagnostic data</strong> (crash reports, performance metrics, and Cataloging
              app diagnostics): deleted after 90 days.
            </span>,
            <span>
              <strong>Help-center search and AI assistant prompts</strong>: retained in
              de-identified form for 90 days for quality improvement, then deleted.
            </span>,
            <span>
              <strong>Marketing communications opt-outs</strong>: retained indefinitely to honor
              your unsubscribe choice.
            </span>,
          ]}
        />
      </PolicySection>

      <PolicySection title="6. Your Rights">
        <p>
          Depending on your jurisdiction, you may have the right to access, correct, or delete your
          personal information; to obtain a portable copy of your data; or to object to or restrict
          certain processing. To exercise these rights, email{' '}
          <a href="mailto:support@auctisync.com">support@auctisync.com</a> from the email address on
          your account. We will respond within 30 days. We may need to retain certain information
          for the retention windows in Section 5 even after an erasure request.
        </p>
        <p>
          California residents have additional rights under the California Consumer Privacy Act
          (CCPA), including the right to know what personal information we collect, the right to
          delete, and the right to opt out of &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of
          personal information. We do not sell or share personal information as defined under the
          CCPA. To exercise CCPA rights, use the same email address above.
        </p>
      </PolicySection>

      <PolicySection title="7. Security">
        <p>
          We use industry-standard security measures to protect your information, including
          encryption in transit (TLS 1.2 or higher), encryption at rest for sensitive data, hashed
          passwords, scoped access tokens, row-level security at the database layer, and continuous
          monitoring for unauthorized access. No method of transmission or storage is 100% secure,
          and we cannot guarantee absolute security.
        </p>
        <p>
          We maintain an offsite, encrypted backup of our primary database (currently rotated daily,
          retained 14 days) with monthly restore-drills into a clean environment, so we can recover
          from infrastructure failures within bounded time.
        </p>
        <p>
          If we become aware of a security incident affecting your personal information, we will
          notify you and applicable regulators in accordance with applicable law.
        </p>
      </PolicySection>

      <PolicySection title="8. Cookies and Similar Technologies">
        <p>
          We use a small number of strictly necessary cookies to keep you signed in, remember your
          preferences, and protect against cross-site request forgery. We do not use third-party
          advertising cookies or cross-site tracking. We do not currently use analytics cookies that
          profile individual users.
        </p>
        <p>
          You can control cookies through your browser settings. Disabling strictly-necessary
          cookies will prevent you from signing in.
        </p>
      </PolicySection>

      <PolicySection title="9. Children's Privacy">
        <p>
          The Services are not intended for individuals under the age of 18. We do not knowingly
          collect personal information from children under 13. If you believe a child under 13 has
          provided personal information to us, please contact{' '}
          <a href="mailto:support@auctisync.com">support@auctisync.com</a> and we will delete it.
        </p>
      </PolicySection>

      <PolicySection title="10. International Users">
        <p>
          Welcome Home Apps is based in the United States, and the infrastructure providers listed
          in Section 4.2 process data primarily in the United States. If you access the Services
          from outside the United States, you understand and consent to your information being
          transferred to and processed in the United States, which may have data-protection laws
          that differ from those of your country.
        </p>
      </PolicySection>

      <PolicySection title="11. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
          at the top reflects the most recent revision. If we make material changes, we will notify
          account holders by email at least 30 days before the changes take effect, and we will post
          the updated policy at this URL.
        </p>
      </PolicySection>

      <PolicySection title="12. Contact Us">
        <p>Questions, requests, or complaints about this Privacy Policy can be sent to:</p>
        <p>
          <strong>Welcome Home Apps LLC</strong>
          <br />
          Attn: Privacy
          <br />
          <a href="mailto:support@auctisync.com">support@auctisync.com</a>
        </p>
      </PolicySection>
    </PolicyPage>
  );
}
